import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { AttachmentQuotation } from '../attachment-quotation';
import { CKEDITOREMAILCONFIG } from '../ckeditor-config';
import { ContactRequestQuotationWithDetail } from '../contact-request-quotation-with-detail';
import { ContactRequestService } from '../service/contact-request.service';
import { EmailSignature } from '../email-signature';
import { GmailMessage } from '../gmail-message';
import { GmailService } from '../service/gmail.service';
import { HttpService } from '../service/http.service';
import { LeadEmailTemplate } from '../lead-email-template';
import { LeadQuotationService } from '../service/lead-quotation.service';
import { ToastMessageService } from '../toast-message.service';
import { ConfirmationService } from 'primeng/api';
import * as Editor from '../../libs/ckeditor';

@Component({
  selector: 'app-contact-request-quotations',
  templateUrl: './contact-request-quotations.component.html',
  styleUrls: ['./contact-request-quotations.component.css']
})
export class ContactRequestQuotationsComponent implements OnInit {

  @Input("requestId")
  requestId!: number;

  @Output()
  cloneQuotationEvent: EventEmitter<ContactRequestQuotationWithDetail> = new EventEmitter();

  quotations: ContactRequestQuotationWithDetail[] = [];
  processing = false;
  displayMailingDialog = false;
  attachmentLoading = false;
  emailSending = false;

  emailForm!: FormGroup;

  emailEditorConfig = CKEDITOREMAILCONFIG;

  emailTemplates: LeadEmailTemplate[] = [];

  activeCkEditor = true;
  selectedQuotation!: ContactRequestQuotationWithDetail | null;

  attachedQuotationFile!: AttachmentQuotation | null;
  public Editor = Editor;

  constructor(
    private httpService: HttpService,
    private confirmationService: ConfirmationService,
    public contactRequestService: ContactRequestService,
    private leadQuotationService: LeadQuotationService,
    private builder: FormBuilder,
    private toastService: ToastMessageService,
    private domSanitizer: DomSanitizer,
    private gmailService: GmailService
  ) { }

  ngOnInit(): void {
    this.initQuotationList();
    this.initEmailForm();
    this.updateEmailSiguatureByStudentLanguage();
  }

  initEmailForm() {
    this.emailForm = this.builder.group({
      subject: "",
      body: "",
      from: null,
      sendTo: null,
      emailTemplate: null,
      signature: null,
      withPastePixel: false
    });
  }

  resetEmailForm() {
    this.emailForm.get("subject")!.setValue(null);
    this.emailForm.get("body")!.patchValue("");
    this.emailForm.get("sendTo")!.setValue(null);
    this.emailForm.get("emailTemplate")!.setValue(null);

  }

  updateEmailSiguatureByStudentLanguage(){
    this.emailForm.get("signature")!.setValue(this.getEmailSignatureByStudentLanguage(this.contactRequestService.emailSignatures));
  }

  getEmailSignatureByStudentLanguage(signatures: EmailSignature[]){
    if(!this.contactRequestService.contactRequest!.language){
      return this.getDefaultEmailSignature(signatures);
    } 
    for(let signature of signatures){
      if(signature.language.id == this.contactRequestService.contactRequest!.language.id){
        return signature;
      }
    }
    return this.getDefaultEmailSignature(signatures);
  }

  getDefaultEmailSignature(signatures: EmailSignature[]){
    if(signatures.length > 0){
      return signatures[0];
    } else {
      return null;
    }
  }

  initQuotationList() {
    this.httpService.getContactRequestQuotationByRequestId(this.requestId).subscribe(res => {
      this.quotations = res;
      console.log(res);
    });
  }

  cloneQuotation(quotation: ContactRequestQuotationWithDetail) {
    this.leadQuotationService.setQuotationRequest(quotation);
    this.cloneQuotationEvent.emit(quotation);
  }

  downloadReport(quotation: ContactRequestQuotationWithDetail) {
    this.processing = true;

    this.httpService.getContactRequestQuotationPdfById(quotation.id!, this.formatLanguageName(this.contactRequestService.contactRequest!.language.name)!).subscribe(blob => {
      let pdfUrl = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = pdfUrl;
      link.download = quotation.description + "_" + this.contactRequestService.contactRequest!.lastName + ".pdf";
      link.click();
      this.processing = false;
    });
  }

  deleteQuotation(quotation: ContactRequestQuotationWithDetail) {
    this.confirmationService.confirm({
      key: "deleteConfirmation",
      message: "Are you sure that you want to delete this quotation?",
      accept: () => {
        this.processing = true;
        this.httpService.deleteContactRequestQuotation(quotation.id!).subscribe(() => {
          this.processing = false;
          this.initQuotationList();
        }, error => this.processing = false);
      }
    });
  }

  formatLanguageName(language: string): string | void {
    if (!language) {
      return;
    }
    switch (language) {
      case "French":
        return "french";
      case "English":
        return "english";
      case "Spanish":
        return "spanish";
      case "Dutch":
        return "dutch";
      case "Portuguese":
        return "portuguese";
    }
  }

  activateEmailDialog(quotation: ContactRequestQuotationWithDetail) {
    this.activeCkEditor = true;
    this.httpService.getLeadEmailTemplatesByLanguage(this.contactRequestService.contactRequest!.language).subscribe(res => this.emailTemplates = res);
    this.displayMailingDialog = true;
    this.loadSelectedQuotation(quotation);
    this.selectedQuotation = quotation;
  }

  closeEmailModal() {
    window.setTimeout(() => {
      this.displayMailingDialog = false;
      this.selectedQuotation = null;
      this.resetEmailForm();
    }, 1);
    this.activeCkEditor = false;
  }

  cancelEmailSending() {
    this.displayMailingDialog = false;
  }

  sendEmailToClient() {
    this.emailSending = true;
    let message = new GmailMessage(
      [this.contactRequestService.contactRequest!.email],
      this.emailForm.value.subject,
      this.emailForm.value.body
    );
    this.httpService.sendHtmlEmailWithContactRequestQuotations(this.gmailService.getFormData(message, [this.attachedQuotationFile!]), 
    this.contactRequestService.contactRequest!.id!, 
    this.emailForm.value.signature?this.emailForm.value.signature.id:null,
    this.emailForm.value.withPastePixel
    ).subscribe(() => {
      this.toastService.addSuccess("Success", "Email sent");
      this.displayMailingDialog = false;
      this.emailSending = false;
    }, error => {
      console.error(error);
      this.toastService.addError("Error", error.message);
      this.emailSending = false;
    });
  }

  loadSelectedQuotation(quotation: ContactRequestQuotationWithDetail) {
    this.attachmentLoading = true;
    this.httpService.getContactRequestQuotationPdfById(quotation.id!, this.contactRequestService.contactRequest!.language.name).subscribe(res => {
      this.attachedQuotationFile = new AttachmentQuotation(quotation.id!, new File([res], this.selectedQuotation!.description + "_" + this.contactRequestService.contactRequest!.lastName + ".pdf"));
      this.attachmentLoading = false;
    }, error => {
      this.toastService.addError("Quotation loading error", error.message);
      this.attachmentLoading = false;
    });
  }

  insertLeadEmailTemplate(template: LeadEmailTemplate) {
    this.httpService.getComplateContactRequestEmailTemplateWithQuotation(template, this.contactRequestService.contactRequest!.id!, this.selectedQuotation!.id!).subscribe(res => {
      this.emailForm.get("subject")!.setValue(res.subject);
      this.emailForm.get("body")!.setValue(res.body);
    }, error => {
      this.toastService.addError("Template loading error", error.message);
    });
  }

  getObjectURL(file: File) {
    return this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  removeAttachmentFile(){
    this.attachedQuotationFile = null;
  }
}
